import { Attr, type ClientSourceValue } from './attributes';
import { resolveEnvironment, resolveRelease, type EnvBag } from './environment';

/**
 * `service.name` values. These are what separate apps in Logfire — there is
 * nothing to create in the UI; a service appears once a span carrying its name
 * arrives.
 *
 * `service.name` comes from the OTel resource, which is per-provider — so
 * apps/api genuinely cannot relabel individual spans, and anything it emits
 * on the extension's behalf is `api`. The extension therefore emits its own
 * OTLP directly (see packages/telemetry/extension/otlp.ts) and carries this
 * resource with it; error records relayed through apps/api remain `api` and
 * are identified by `firmable.client_source`.
 *
 * Unprefixed: the project is already Firmable's and every span carries
 * `service.namespace: 'firmable'`, so a `firmable-` prefix repeated the
 * obvious and cost width in every trace list and service selector.
 */
export const SERVICE_NAMES = {
  web: 'web',
  api: 'api',
  agents: 'agents',
  extension: 'browser-extension',
  // Deferred apps, pre-registered so adding them later is a one-line change.
  admin: 'admin',
  hubspot: 'hubspot',
  mobile: 'mobile',
} as const;

export type ServiceKey = keyof typeof SERVICE_NAMES;

export interface ResourceOptions {
  service: ServiceKey;
  env: EnvBag;
  clientSource?: ClientSourceValue;
  extra?: Record<string, string | number | boolean>;
}

export function buildResourceAttributes(options: ResourceOptions): Record<string, string | number | boolean> {
  const { service, env, clientSource, extra } = options;
  const environment = resolveEnvironment(env);

  return {
    'service.name': SERVICE_NAMES[service],
    'service.namespace': 'firmable',
    'service.version': resolveRelease(env),
    // The semconv >=1.27 name. Logfire surfaces it as the `deployment_environment` column.
    'deployment.environment.name': environment,
    ...(env.VERCEL_DEPLOYMENT_ID ? { [Attr.DEPLOYMENT_ID]: env.VERCEL_DEPLOYMENT_ID } : {}),
    ...(clientSource ? { [Attr.CLIENT_SOURCE]: clientSource } : {}),
    ...extra,
  };
}
